import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { cachePreferences, getCachedPreferences, UserPreferences } from '../lib/preferences';

// What may be sent when saving: only the parts that change
export type PreferencesInput = {
  startPage?: string;
  rowsPerPage?: number;
  notifications?: { popups?: boolean; categories?: Partial<UserPreferences['notifications']['categories']> };
};

export const fetchPreferences = async (): Promise<UserPreferences> => {
  const response = await api.get('/users/preferences');
  cachePreferences(response.data);
  return response.data;
};

// The preferences of the signed-in user; the copy in the browser is shown until the server answers
export const usePreferences = (enabled = true) => {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    enabled,
    placeholderData: getCachedPreferences,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PreferencesInput): Promise<UserPreferences> => {
      const response = await api.patch('/users/preferences', input);
      return response.data;
    },
    onSuccess: (saved) => {
      cachePreferences(saved);
      queryClient.setQueryData(['preferences'], saved);
    },
  });
};
